/**
 * The door to CenoX AI.
 *
 * Entitlement decides what is behind it, and the server decides entitlement.
 * A workspace that holds CenoX AI and has initialized it gets the workspace;
 * every other workspace gets the same honest landing page every other locked
 * product gets — because "you have not bought this" is not a thing the AI
 * workspace should be rendering around.
 *
 * The check is `availableAction === 'open'`, not a rule reassembled here from
 * `entitled && activated && enabled`. There is one implementation of that
 * rule and it is on the server.
 *
 * Two doors share it. `/ai/<section>` is the product's sections; `/chat` is
 * Chat, which used to be `/ai` itself. Bare `/ai` now forwards to `/chat` for a
 * workspace that holds the product, so a bookmark to the old address still
 * opens the conversation list — and still lands on the product page for one
 * that does not.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { usePlatformProducts } from '../../lib/platformProducts';
import { CenoxShell } from '../../layouts/CenoxShell';
import { ProductPage } from '../products/ProductPage';
import { AiWorkspace } from './AiWorkspace';
import { ChatView } from './ChatView';
import { Card, Spinner } from '../../components/ui';

/** What the server says about CenoX AI for this workspace, as a page state. */
function useAiAccess(): 'pending' | 'error' | 'open' | 'locked' {
  const products = usePlatformProducts();
  if (products.isPending) return 'pending';
  if (products.isError) return 'error';
  const ai = products.data.data.find((p) => p.product === 'ai');
  return ai?.availableAction === 'open' ? 'open' : 'locked';
}

function Checking(): ReactNode {
  return (
    <div className="grid min-h-dvh place-items-center">
      <Spinner label="Opening CenoX AI" />
    </div>
  );
}

/*
  A failed request is not an entitlement answer.

  Falling through to the locked page here would tell somebody who pays for
  CenoX AI that they do not have it, on a bad connection.
*/
function CouldNotCheck(): ReactNode {
  return (
    <CenoxShell>
      <Card>
        <p className="text-sm text-slate-900">Could not check this workspace&rsquo;s access.</p>
        <p className="mt-1 text-sm text-slate-500">
          This is a connection problem, not a change to what you have access to.
        </p>
      </Card>
    </CenoxShell>
  );
}

export function AiEntry(): ReactNode {
  const { section } = useParams<{ section: string }>();
  const { search } = useLocation();
  const access = useAiAccess();

  if (access === 'pending') return <Checking />;
  if (access === 'error') return <CouldNotCheck />;
  if (access === 'locked') return <ProductPage />;

  // `/ai` was Chat. It forwards rather than renders, so a conversation has one
  // address and the sidebar highlights the right item.
  if (!section) return <Navigate to={`/chat${search}`} replace />;

  return (
    <CenoxShell>
      <AiWorkspace />
    </CenoxShell>
  );
}

/**
 * `/chat` and `/chat/:conversationId`.
 *
 * Locked, `/chat` still shows Chat's welcome — composer shut, and a notice
 * whose "Get access" leads to the CenoX AI product page, which runs the real
 * trial or upgrade flow. A locked `/chat/<id>` returns to `/chat`: a thread
 * is not something a workspace without the product can open.
 */
export function ChatEntry(): ReactNode {
  const access = useAiAccess();
  const { conversationId } = useParams<{ conversationId: string }>();

  if (access === 'pending') return <Checking />;
  if (access === 'error') return <CouldNotCheck />;
  // A thread cannot be opened without the product; its list is not even asked for.
  if (access === 'locked' && conversationId) return <Navigate to="/chat" replace />;

  return (
    <CenoxShell rail={false}>
      <ChatView access={access} />
    </CenoxShell>
  );
}
